import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '../ui/Button';

const HOUR = 60 * 60 * 1000;
/** A new version found this soon after opening or returning to the app is applied at once. */
export const FRESH_MS = 10_000;

/**
 * Checks for a new version on open, every hour, and whenever the app comes back to the
 * foreground (an installed PWA is mostly resumed, not reopened, and timers stop in the
 * background). A version found while the app was just opened or resumed is applied right away,
 * before anything has been typed; one found mid-use shows the banner instead, so a reload never
 * throws away a half-filled form. Unsynced changes live in the on-device outbox and survive it.
 */
export function UpdatePrompt() {
  const freshSince = useRef(Date.now());
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => void registration.update().catch(() => {});
      setInterval(check, HOUR);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        freshSince.current = Date.now();
        check();
      });
      window.addEventListener('online', check);
    },
  });

  useEffect(() => {
    if (needRefresh && Date.now() - freshSince.current < FRESH_MS) {
      void updateServiceWorker(true);
    }
  }, [needRefresh, updateServiceWorker]);

  if (!needRefresh) return null;
  return (
    <div
      role="status"
      className="fixed inset-x-4 top-[calc(12px+env(safe-area-inset-top))] z-50 mx-auto flex max-w-lg items-center lg:left-[calc(15rem+16px)] gap-2 rounded-[14px] border border-border bg-surface-2 p-3 text-sm"
    >
      <p className="flex-1">A new version of Tahi is ready.</p>
      <Button variant="primary" onClick={() => void updateServiceWorker(true)}>
        Update
      </Button>
      <Button variant="ghost" onClick={() => setNeedRefresh(false)}>
        Later
      </Button>
    </div>
  );
}
